import { useEffect, useState } from 'react';
import './Login.css';
import LoadingIndicator from './LoadingIndicator';

const emptyErrors = {
  username: '',
  password: '',
};

export default function Login({ onLogin, onShowRegister }) {
  const [form, setForm] = useState({ username: '', password: '' });
  const [errors, setErrors] = useState(emptyErrors);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [needsForceLogin, setNeedsForceLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erpLoading, setErpLoading] = useState(false);
  const [showLocalLogin, setShowLocalLogin] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const erpError = params.get('erp_login_error') || params.get('error_description') || params.get('error');

    if (erpError) {
      setMessage(erpError);
      setMessageType('error');
      window.history.replaceState({}, '', '/login');
      return;
    }

    if (!code) return;

    let cancelled = false;

    async function completeErpLogin() {
      setErpLoading(true);
      setMessage('');
      setMessageType('');

      try {
        const callbackParams = new URLSearchParams({ code });
        if (state) callbackParams.set('state', state);

        const res = await fetch(`/login/erpnext/callback?${callbackParams.toString()}`, {
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));

        if (cancelled) return;

        if (!res.ok || !data.success) {
          setMessage(data.message || 'ERPNext login failed');
          setMessageType('error');
          window.history.replaceState({}, '', '/login');
          return;
        }

        window.history.replaceState({}, '', '/tasks');
        onLogin({ user: data.user, accessToken: '' });
      } catch {
        if (!cancelled) {
          setMessage('ERPNext login could not be completed. Please try again.');
          setMessageType('error');
          window.history.replaceState({}, '', '/login');
        }
      } finally {
        if (!cancelled) setErpLoading(false);
      }
    }

    completeErpLogin();

    return () => {
      cancelled = true;
    };
  }, [onLogin]);

  function onChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
    setMessage('');
    setNeedsForceLogin(false);
  }

  function validate() {
    const nextErrors = { ...emptyErrors };

    if (!form.username.trim()) {
      nextErrors.username = 'Username is required';
    }

    if (!form.password) {
      nextErrors.password = 'Password is required';
    }

    setErrors(nextErrors);
    return !nextErrors.username && !nextErrors.password;
  }

  async function submitLogin(force = false) {
    if (!validate()) return;

    setLoading(true);
    setMessage('');
    setMessageType('');

    try {
      const credentials = btoa(`${form.username.trim()}:${form.password}`);

const res = await fetch(force ? '/login/force-login' : '/login/authenticate', {
  method: 'POST',
  credentials: 'include',
  headers: { 
    'Content-Type': 'application/json',
    'Authorization': `Basic ${credentials}`
  },
  body: JSON.stringify({}), // empty body
});
      const data = await res.json();

      if (data.action_needed) {
        setNeedsForceLogin(true);
        setMessage(data.message || 'This user is already logged in elsewhere.');
        setMessageType('warning');
        return;
      }

      if (!res.ok || !data.success) {
        setMessage(data.message || 'Login failed');
        setMessageType('error');
        return;
      }

      onLogin({
        user: data.user || { username: form.username.trim() },
        accessToken: data.accessToken || '',
      });
    } catch {
      setMessage('Network error. Please try again.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  }

  function onSubmit(e) {
    e.preventDefault();
    submitLogin(false);
  }

  function startErpNextLogin() {
    setErpLoading(true);
    window.location.href = '/login/erpnext';
  }

  return (
    <main className="login-page">
      <section className="login-card" aria-labelledby="login-title">
        <div className="login-brand">
          <div className="login-logo">T</div>
          <div>
            <p>TaskFlow</p>
            <span>Secure workspace</span>
          </div>
        </div>

        <h1 id="login-title">Sign in</h1>

        <button className="login-button erp-login-button" type="button" onClick={startErpNextLogin} disabled={erpLoading}>
          {erpLoading ? <LoadingIndicator variant="button" label="Opening ERPNext..." /> : 'Continue with ERPNext'}
        </button>

        <button
          className="local-login-toggle"
          type="button"
          onClick={() => setShowLocalLogin((prev) => !prev)}
          disabled={erpLoading}
        >
          {showLocalLogin ? 'Hide local login' : 'Use local TMS password'}
        </button>

        {showLocalLogin ? (
          <form onSubmit={onSubmit} noValidate>
            <div className="login-field">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                name="username"
                value={form.username}
                onChange={onChange}
                placeholder="Enter username"
                autoComplete="username"
                aria-invalid={Boolean(errors.username)}
                disabled={loading || erpLoading}
              />
              {errors.username && <span className="field-error">{errors.username}</span>}
            </div>

            <div className="login-field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                value={form.password}
                onChange={onChange}
                placeholder="Enter password"
                autoComplete="current-password"
                aria-invalid={Boolean(errors.password)}
                disabled={loading || erpLoading}
              />
              {errors.password && <span className="field-error">{errors.password}</span>}
            </div>

            <button className="login-button" type="submit" disabled={loading || erpLoading}>
              {loading ? <LoadingIndicator variant="button" label="Signing in..." /> : 'Sign in locally'}
            </button>

            {needsForceLogin && (
              <button
                className="force-login-button"
                type="button"
                disabled={loading || erpLoading}
                onClick={() => submitLogin(true)}
              >
                {loading ? <LoadingIndicator variant="button" label="Signing in..." /> : 'Continue here'}
              </button>
            )}
          </form>
        ) : null}

        {message && <div className={`login-message ${messageType}`}>{message}</div>}

        {/* <p className="auth-switch">
          New user?
          <button type="button" onClick={onShowRegister}>
            Create account
          </button>
        </p> */}
      </section>
    </main>
  );
}
